import { z } from 'zod';
import { UsageSchema } from './account';
import { LANGUAGES, LENGTHS, MAX_INSTRUCTION_LENGTH, TONES } from './options';
import { MAX_INSTRUCTIONS_PER_REWRITE } from './saved-instructions';
import { MAX_TEXT_LENGTH, countCharacters } from './text';

/**
 * The four rewrites, ordered from the lightest touch to the most polished result. The field
 * descriptions double as instructions to the model and as API documentation; parsing trims
 * stray whitespace from model output.
 */
export const RewritesSchema = z.object({
  minorGrammarFixes: z
    .string()
    .trim()
    .describe(
      'Only grammar, spelling, and punctuation are corrected, with the smallest possible changes.',
    ),
  naturalRewording: z
    .string()
    .trim()
    .describe(
      'Small rephrasings that make the text sound more natural while staying close to the original wording.',
    ),
  enhancedClarity: z
    .string()
    .trim()
    .describe('Freer rephrasing that improves clarity, fluency, and readability.'),
  optimalVersion: z
    .string()
    .trim()
    .describe(
      "The clearest, most effective version for the writer's purpose, with natural wording and an appropriate tone.",
    ),
});

export type Rewrites = z.infer<typeof RewritesSchema>;
export type RewriteTier = keyof Rewrites;

/** Rewrite tiers in display order. */
export const REWRITE_TIERS: readonly RewriteTier[] = RewritesSchema.keyof().options;

const formattedLimit = MAX_TEXT_LENGTH.toLocaleString('en-US');

export const RewriteOptionsSchema = z
  .object({
    tone: z
      .enum(TONES)
      .default('natural')
      .describe('The tone to write in; "natural" keeps the writer’s own.'),
    customTone: z
      .string()
      .min(1)
      .max(64)
      .optional()
      .describe(
        'The id of one of the caller’s custom tones, used in place of "tone". Its rules apply to every version. Requires signing in, or an API key.',
      ),
    length: z.enum(LENGTHS).default('same'),
    language: z
      .enum(LANGUAGES)
      .default('auto')
      .describe('The language to write in; "auto" keeps the text’s own.'),
    instruction: z
      .string()
      .trim()
      .max(MAX_INSTRUCTION_LENGTH)
      .optional()
      .describe('Extra guidance in the writer’s words, such as “make it persuasive”.'),
    savedInstructions: z
      .array(z.string().min(1).max(64))
      .max(MAX_INSTRUCTIONS_PER_REWRITE)
      .optional()
      .describe(
        `The ids of up to ${String(MAX_INSTRUCTIONS_PER_REWRITE)} of the caller’s saved instructions, followed along with "instruction". Requires signing in, or an API key.`,
      ),
  })
  .describe(
    'How to steer the rewrites. Tone, length, and instructions shape every version but the minor grammar fixes.',
  )
  .meta({ id: 'RewriteOptions' });

/** Most of the writing on either side of a passage that a rewrite takes as context. */
export const MAX_CONTEXT_LENGTH = 1_000;

export const PassageContextSchema = z
  .object({
    before: z
      .string()
      .max(MAX_CONTEXT_LENGTH)
      .optional()
      .describe('The writing just before the passage.'),
    after: z
      .string()
      .max(MAX_CONTEXT_LENGTH)
      .optional()
      .describe('The writing just after the passage.'),
  })
  .describe(
    `When "text" is a passage of longer writing, such as a selection: up to ${MAX_CONTEXT_LENGTH.toLocaleString('en-US')} characters on either side of it, so its versions fit in. Only "text" is rewritten, and only its words use credits.`,
  )
  .meta({ id: 'PassageContext' });

export type PassageContext = z.infer<typeof PassageContextSchema>;

export const RewriteRequestSchema = z
  .object({
    text: z
      .string()
      .refine((text) => text.trim().length > 0, 'Text must not be empty.')
      .refine(
        (text) => countCharacters(text) <= MAX_TEXT_LENGTH,
        `Text must be at most ${formattedLimit} characters.`,
      )
      .describe(`The text to rewrite: up to ${formattedLimit} characters, in any language.`),
    options: RewriteOptionsSchema.optional(),
    context: PassageContextSchema.optional(),
  })
  .meta({ id: 'RewriteRequest' });

export const RewriteResponseSchema = z
  .object({
    rewrites: RewritesSchema,
    usage: UsageSchema.describe('Daily word usage after this rewrite.'),
  })
  .meta({ id: 'RewriteResponse' });

export type RewriteRequest = z.infer<typeof RewriteRequestSchema>;
export type RewriteResponse = z.infer<typeof RewriteResponseSchema>;
